import { Injectable } from '@angular/core';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import { from, of } from 'rxjs';
import { switchMap, map, catchError, take, finalize } from 'rxjs/operators';
import * as ConfigSetupActions from './config-setup.actions';
import { AppNotificationUtilClass, MachineMaintenanceDataService } from '@monorepo/services';
import { ConfigSetupFacade } from './config-setup.facade';
import { TConfigSetupActionType } from './config-setup.models';

@Injectable({ providedIn: 'root' })
export class ConfigSetupEffects {
  constructor(
    private actions$: Actions,
    private mmDataService: MachineMaintenanceDataService,
    private configSetupFacade: ConfigSetupFacade,
    private APIHelper: AppNotificationUtilClass,
  ) {}

  getHolidayList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TConfigSetupActionType.GetHolidayList),
      switchMap((action: ConfigSetupActions.GetHolidayListInfo) =>
        this.configSetupFacade.getHolidayListPagination().pipe(
          take(1),
          map((pagination) => {
            return [action, pagination] as const;
          }),
        ),
      ),
      switchMap((args) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, ...args] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action, pagination]) =>
        this.mmDataService
          .fetchHolidayListData({
            page: pagination.pageNo,
            itemsPerPage: pagination.pageSize,
            selectedFilters: action.selectedFilters,
          })
          .pipe(
            finalize(() => {
              this.APIHelper.dismissLoadingV2(loaderEl);
            }),
            map((res) => new ConfigSetupActions.GetHolidayListInfoSuccess(res, action.fetchReason)),
            catchError(() => {
              return of(new ConfigSetupActions.GetHolidayListInfoFailure());
            }),
          ),
      ),
    ),
  );

  addHolidays$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TConfigSetupActionType.AddHolidays),
      switchMap((action: ConfigSetupActions.AddHolidays) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.mmDataService.addHolidays(action.holidays).pipe(
          finalize(() => {
            this.APIHelper.dismissLoadingV2(loaderEl);
          }),
          map(() => {
            this.configSetupFacade.resetHolidayListInfo('ACTION');
            return new ConfigSetupActions.AddHolidaysSuccess();
          }),
          catchError(() => {
            return of(new ConfigSetupActions.AddHolidaysFailure());
          }),
        ),
      ),
    ),
  );

  deleteHolidays$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TConfigSetupActionType.DeleteHolidays),
      switchMap((action: ConfigSetupActions.DeleteHolidays) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.mmDataService.deleteHolidays(action.holidays).pipe(
          finalize(() => {
            this.APIHelper.dismissLoadingV2(loaderEl);
          }),
          map(() => {
            this.configSetupFacade.resetHolidayListInfo('ACTION');
            return new ConfigSetupActions.DeleteHolidaysSuccess();
          }),
          catchError(() => {
            return of(new ConfigSetupActions.DeleteHolidaysFailure());
          }),
        ),
      ),
    ),
  );

  editHoliday$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TConfigSetupActionType.EditHoliday),
      switchMap((action: ConfigSetupActions.EditHoliday) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.mmDataService.editHoliday(action.holiday).pipe(
          finalize(() => {
            this.APIHelper.dismissLoadingV2(loaderEl);
          }),
          map(() => {
            this.configSetupFacade.resetHolidayListInfo('ACTION');
            return new ConfigSetupActions.EditHolidaySuccess();
          }),
          catchError(() => {
            return of(new ConfigSetupActions.EditHolidayFailure());
          }),
        ),
      ),
    ),
  );
}
